import type { AdminMe, AdminSearchHit, EffectiveContentResponse } from "@vital/shared";

let csrfToken: string | null = null;

async function fetchCsrf(): Promise<string> {
  const res = await fetch("/api/admin/csrf", { credentials: "include" });
  if (!res.ok) {
    let message = `Failed to fetch CSRF token (HTTP ${res.status})`;
    try {
      const err = (await res.json()) as { error?: string; message?: string };
      if (err.error) message = err.error;
      else if (err.message) message = err.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status);
  }
  const data = (await res.json()) as { token: string };
  csrfToken = data.token;
  return data.token;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: { error?: string; currentVersion?: number },
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { csrf?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (options?.csrf !== false && method !== "GET" && method !== "HEAD") {
    const token = csrfToken ?? (await fetchCsrf());
    headers["X-CSRF-Token"] = token;
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let message = res.statusText;
    let errBody: { error?: string; currentVersion?: number } | undefined;
    try {
      errBody = (await res.json()) as { error?: string; currentVersion?: number };
      if (errBody.error) message = errBody.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status, errBody);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export type LoginResponse =
  | { status: "authenticated"; user: AdminMe }
  | {
      status: "mfa_enrollment_required";
      enrollmentToken: string;
      otpauthUri: string;
      secret: string;
    }
  | { status: "mfa_required"; challengeToken: string };

export type MfaEnrollResponse = {
  status: "authenticated";
  recoveryCodes: string[];
  user: AdminMe;
};

export const api = {
  getMe: () => request<AdminMe>("GET", "/api/admin/me"),

  selectOrganization: (organizationId: string) =>
    request<AdminMe>("POST", "/api/admin/auth/selected-organization", { organizationId }),

  login: (email: string, password: string) =>
    request<LoginResponse>("POST", "/api/admin/auth/login", { email, password }, { csrf: false }),

  mfaEnroll: (enrollmentToken: string, code: string) =>
    request<MfaEnrollResponse>(
      "POST",
      "/api/admin/auth/mfa/enroll",
      { enrollmentToken, code },
      { csrf: false },
    ),

  mfaVerify: (challengeToken: string, code: string) =>
    request<{ status: "authenticated"; user: AdminMe }>(
      "POST",
      "/api/admin/auth/mfa/verify",
      { challengeToken, code },
      { csrf: false },
    ),

  logout: () => request<{ ok: boolean }>("POST", "/api/admin/auth/logout"),

  listSites: () =>
    request<Array<{ id: string; code: string; name: string; location: string | null }>>(
      "GET",
      "/api/admin/sites",
    ),

  listSiteGroups: () =>
    request<
      Array<{
        id: string;
        name: string;
        description: string;
        active: boolean;
        members: Array<{ siteId: string; siteCode: string; siteName: string }>;
        createdAt: string;
        updatedAt: string;
      }>
    >("GET", "/api/admin/site-groups"),

  createSiteGroup: (data: { name: string; description?: string; siteIds?: string[] }) =>
    request<{ id: string }>("POST", "/api/admin/site-groups", data),

  updateSiteGroup: (
    id: string,
    data: Partial<{ name: string; description: string; siteIds: string[]; active: boolean }>,
  ) => request<{ ok: boolean }>("PATCH", `/api/admin/site-groups/${id}`, data),

  getEffectiveContent: (params: { siteId?: string; siteCode?: string }) => {
    const qs = new URLSearchParams();
    if (params.siteId) qs.set("siteId", params.siteId);
    if (params.siteCode) qs.set("siteCode", params.siteCode);
    return request<EffectiveContentResponse>(
      "GET",
      `/api/admin/content/effective?${qs.toString()}`,
    );
  },

  listSiteGroupOverlaps: () =>
    request<{
      overlaps: Array<{
        siteId: string;
        siteCode: string;
        organizationId: string;
        groups: Array<{ id: string; name: string; active: boolean }>;
      }>;
    }>("GET", "/api/admin/site-groups/overlaps"),

  resolveSiteGroupOverlaps: (mappings: Array<{ siteId: string; keepGroupId: string }>) =>
    request<{
      ok: boolean;
      removed: number;
      overlaps: Array<{
        siteId: string;
        siteCode: string;
        organizationId: string;
        groups: Array<{ id: string; name: string; active: boolean }>;
      }>;
    }>("POST", "/api/admin/site-groups/resolve-overlaps", { mappings }),

  listAnnouncements: () =>
    request<
      Array<{
        id: string;
        state: string;
        scope: string;
        title: string;
        body: string;
        severity: string;
        dismissible: boolean;
        priority: number;
        emergency: boolean;
        emergencyAudience: string | null;
        rejectionReason: string | null;
        submittedAt: string | null;
        pausedFromState: string | null;
        siteCode: string | null;
        siteName: string | null;
        groupId: string | null;
        groupName: string | null;
        startsAt: string | null;
        endsAt: string | null;
        createdAt: string;
        updatedAt: string;
      }>
    >("GET", "/api/admin/content/announcements"),

  createAnnouncement: (data: {
    title: string;
    body: string;
    severity: string;
    dismissible: boolean;
    targetScope?: string;
    siteCode?: string;
    groupId?: string;
    priority?: number;
    startsAt?: string | null;
    endsAt?: string | null;
    emergency?: boolean;
    emergencyAudience?: "organization" | "platform";
  }) => request<{ id: string }>("POST", "/api/admin/content/announcements", data),

  submitAnnouncement: (id: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/content/announcements/${id}/submit`, {}),

  rejectAnnouncement: (id: string, reason: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/content/announcements/${id}/reject`, {
      reason,
    }),

  pauseAnnouncement: (id: string, password: string) =>
    request<{ ok: boolean; pausedFromState: string }>(
      "POST",
      `/api/admin/content/announcements/${id}/pause`,
      { password },
    ),

  unpauseAnnouncement: (id: string, password: string) =>
    request<{ ok: boolean; state: string }>(
      "POST",
      `/api/admin/content/announcements/${id}/unpause`,
      { password },
    ),

  getAnnouncementDiff: (id: string) =>
    request<ContentDraftDiff>("GET", `/api/admin/content/announcements/${id}/diff`),

  publishAnnouncement: (id: string, password: string) =>
    request<{ ok: boolean; publishedAt: string; state: string }>(
      "POST",
      `/api/admin/content/announcements/${id}/publish`,
      { password },
    ),

  archiveAnnouncement: (id: string, password: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/content/announcements/${id}/archive`, {
      password,
    }),

  getKillSwitchState: () =>
    request<KillSwitchAdminState>("GET", "/api/admin/settings/remote-content-kill-switch"),

  enableKillSwitch: (data: { password: string; reason?: string; expiresAt?: string | null }) =>
    request<KillSwitchAdminState>(
      "POST",
      "/api/admin/settings/remote-content-kill-switch/enable",
      data,
    ),

  disableKillSwitch: (password: string) =>
    request<KillSwitchAdminState>(
      "POST",
      "/api/admin/settings/remote-content-kill-switch/disable",
      { password },
    ),

  listApprovedCtaUrls: () =>
    request<{ urls: string[]; updatedAt: string | null }>(
      "GET",
      "/api/admin/settings/approved-cta-urls",
    ).then((r) => r.urls),

  getApprovedCtaSettings: () =>
    request<{ urls: string[]; updatedAt: string | null }>(
      "GET",
      "/api/admin/settings/approved-cta-urls",
    ),

  setApprovedCtaUrls: (urls: string[]) =>
    request<{ urls: string[]; updatedAt: string | null }>(
      "PUT",
      "/api/admin/settings/approved-cta-urls",
      { urls },
    ),

  listPromotions: () =>
    request<
      Array<{
        id: string;
        state: string;
        scope: string;
        title: string;
        body: string;
        badge: string;
        ctaType: string | null;
        ctaLabel: string;
        ctaHref: string | null;
        imagePath: string | null;
        imageAlt: string;
        priority: number;
        rejectionReason: string | null;
        submittedAt: string | null;
        pausedFromState: string | null;
        siteCode: string | null;
        siteName: string | null;
        groupId: string | null;
        groupName: string | null;
        startsAt: string | null;
        endsAt: string | null;
        createdAt: string;
        updatedAt: string;
      }>
    >("GET", "/api/admin/content/promotions"),

  createPromotion: (data: {
    title: string;
    body: string;
    badge?: string;
    targetScope?: string;
    siteCode?: string;
    groupId?: string;
    priority?: number;
    ctaType?: string | null;
    ctaLabel?: string;
    customApprovedUrl?: string;
    imagePath?: string | null;
    imageAlt?: string;
    startsAt?: string | null;
    endsAt?: string | null;
  }) => request<{ id: string }>("POST", "/api/admin/content/promotions", data),

  updatePromotion: (
    id: string,
    data: Partial<{
      title: string;
      body: string;
      badge: string;
      targetScope: string;
      siteCode: string;
      groupId: string;
      priority: number;
      ctaType: string | null;
      ctaLabel: string;
      customApprovedUrl: string;
      imagePath: string | null;
      imageAlt: string;
      startsAt: string | null;
      endsAt: string | null;
    }>,
  ) => request<{ ok: boolean }>("PATCH", `/api/admin/content/promotions/${id}`, data),

  publishPromotion: (id: string, password: string) =>
    request<{ ok: boolean; publishedAt: string; version: number; state: string }>(
      "POST",
      `/api/admin/content/promotions/${id}/publish`,
      { password },
    ),

  archivePromotion: (id: string, password: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/content/promotions/${id}/archive`, { password }),

  submitPromotion: (id: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/content/promotions/${id}/submit`, {}),

  rejectPromotion: (id: string, reason: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/content/promotions/${id}/reject`, { reason }),

  pausePromotion: (id: string, password: string) =>
    request<{ ok: boolean; pausedFromState: string }>(
      "POST",
      `/api/admin/content/promotions/${id}/pause`,
      { password },
    ),

  unpausePromotion: (id: string, password: string) =>
    request<{ ok: boolean; state: string }>(
      "POST",
      `/api/admin/content/promotions/${id}/unpause`,
      { password },
    ),

  getPromotionDiff: (id: string) =>
    request<ContentDraftDiff>("GET", `/api/admin/content/promotions/${id}/diff`),

  listPromotionSnapshots: (id: string) =>
    request<
      Array<{
        id: string;
        version: number;
        publishedAt: string;
        payloadChecksum: string;
        publishedPayload: unknown;
      }>
    >("GET", `/api/admin/content/promotions/${id}/snapshots`),

  uploadPromotionImage: async (file: File) => {
    const token = csrfToken ?? (await fetchCsrf());
    const form = new FormData();
    form.append("file", file);
    const res = await fetch("/api/admin/uploads/promotion-image", {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const err = (await res.json()) as { error?: string };
        if (err.error) message = err.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(message, res.status);
    }
    return res.json() as Promise<{ path: string }>;
  },

  listAdvertisers: () =>
    request<
      Array<{
        id: string;
        displayName: string;
        internalNotes: string | null;
        status: "active" | "archived";
        createdAt: string;
        updatedAt: string;
      }>
    >("GET", "/api/admin/advertisers"),

  createAdvertiser: (data: { displayName: string; internalNotes?: string | null }) =>
    request<{ id: string }>("POST", "/api/admin/advertisers", data),

  updateAdvertiser: (
    id: string,
    data: Partial<{ displayName: string; internalNotes: string | null }>,
  ) => request<{ ok: boolean }>("PATCH", `/api/admin/advertisers/${id}`, data),

  archiveAdvertiser: (id: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/advertisers/${id}/archive`, {}),

  listSponsoredCampaigns: () =>
    request<
      Array<{
        id: string;
        advertiserId: string;
        advertiserName: string;
        advertiserStatus: string;
        name: string;
        placement: string;
        scope: string;
        priority: number;
        state: string;
        pausedFromState: string | null;
        startsAt: string;
        endsAt: string;
        rejectionReason: string | null;
        submittedAt: string | null;
        headline: string;
        description: string;
        offerText: string | null;
        sponsorshipLabel: string;
        imagePath: string;
        imageAlt: string;
        ctaType: string | null;
        ctaLabel: string;
        ctaHref: string;
        siteCode: string | null;
        siteName: string | null;
        groupId: string | null;
        groupName: string | null;
        createdAt: string;
        updatedAt: string;
      }>
    >("GET", "/api/admin/sponsored-campaigns"),

  createSponsoredCampaign: (data: {
    advertiserId: string;
    name: string;
    headline: string;
    description: string;
    offerText?: string | null;
    sponsorshipLabel?: string;
    imagePath: string;
    imageAlt: string;
    ctaType: string;
    ctaLabel: string;
    customApprovedUrl?: string;
    targetScope?: string;
    siteCode?: string;
    groupId?: string;
    priority?: number;
    startsAt: string;
    endsAt: string;
  }) => request<{ id: string }>("POST", "/api/admin/sponsored-campaigns", data),

  updateSponsoredCampaign: (
    id: string,
    data: Partial<{
      advertiserId: string;
      name: string;
      headline: string;
      description: string;
      offerText: string | null;
      sponsorshipLabel: string;
      imagePath: string;
      imageAlt: string;
      ctaType: string;
      ctaLabel: string;
      customApprovedUrl: string;
      targetScope: string;
      siteCode: string;
      groupId: string;
      priority: number;
      startsAt: string;
      endsAt: string;
    }>,
  ) => request<{ ok: boolean }>("PATCH", `/api/admin/sponsored-campaigns/${id}`, data),

  submitSponsoredCampaign: (id: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/sponsored-campaigns/${id}/submit`, {}),

  rejectSponsoredCampaign: (id: string, reason: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/sponsored-campaigns/${id}/reject`, { reason }),

  publishSponsoredCampaign: (id: string, password: string) =>
    request<{ ok: boolean; publishedAt: string; version: number; state: string }>(
      "POST",
      `/api/admin/sponsored-campaigns/${id}/publish`,
      { password },
    ),

  pauseSponsoredCampaign: (id: string, password: string) =>
    request<{ ok: boolean; pausedFromState: string }>(
      "POST",
      `/api/admin/sponsored-campaigns/${id}/pause`,
      { password },
    ),

  unpauseSponsoredCampaign: (id: string, password: string) =>
    request<{ ok: boolean; state: string }>(
      "POST",
      `/api/admin/sponsored-campaigns/${id}/unpause`,
      { password },
    ),

  archiveSponsoredCampaign: (id: string, password: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/sponsored-campaigns/${id}/archive`, { password }),

  listSponsoredSnapshots: (id: string) =>
    request<
      Array<{
        id: string;
        version: number;
        publishedAt: string;
        publishedBy: string;
        payloadChecksum: string;
        publishedPayload: unknown;
        rollbackOfSnapshotId: string | null;
      }>
    >("GET", `/api/admin/sponsored-campaigns/${id}/snapshots`),

  rollbackSponsoredCampaign: (id: string, snapshotId: string, password: string) =>
    request<{ ok: boolean; version: number }>(
      "POST",
      `/api/admin/sponsored-campaigns/${id}/rollback`,
      { snapshotId, password },
    ),

  uploadSponsoredImage: async (file: File) => {
    const token = csrfToken ?? (await fetchCsrf());
    const form = new FormData();
    form.append("file", file);
    const res = await fetch("/api/admin/uploads/sponsored-image", {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const err = (await res.json()) as { error?: string };
        if (err.error) message = err.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(message, res.status);
    }
    return res.json() as Promise<{ path: string }>;
  },

  searchAdmin: (q: string) => {
    const qs = new URLSearchParams({ q });
    return request<{ items: AdminSearchHit[] }>("GET", `/api/admin/search?${qs.toString()}`);
  },

  getDashboardSummary: () =>
    request<{
      draftCount: number;
      publishedAnnouncements: number;
      publishedPromotions: number;
      scheduledCount: number;
      recentAudit: Array<{
        id: string;
        action: string;
        summary: string;
        createdAt: string;
        actor: { id: string; displayName: string; email: string } | null;
      }>;
    }>("GET", "/api/admin/dashboard/summary"),

  listAudit: (params?: { limit?: number; offset?: number; action?: string }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    if (params?.action) qs.set("action", params.action);
    const q = qs.toString();
    return request<{
      items: Array<{
        id: string;
        action: string;
        entityType: string;
        entityId: string | null;
        summary: string;
        ipAddress: string | null;
        createdAt: string;
        actor: { id: string; displayName: string; email: string } | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>("GET", `/api/admin/audit${q ? `?${q}` : ""}`);
  },

  listSnapshots: (params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return request<{
      items: Array<{
        id: string;
        contentItemId: string;
        kind: string;
        title: string;
        version: number;
        publishedAt: string;
        publishedBy: string;
        payloadChecksum: string;
        rollbackOfSnapshotId: string | null;
      }>;
      total: number;
    }>("GET", `/api/admin/content/snapshots${q ? `?${q}` : ""}`);
  },

  rollbackPromotion: (id: string, snapshotId: string, password: string) =>
    request<{ ok: boolean; version: number }>(
      "POST",
      `/api/admin/content/promotions/${id}/rollback`,
      { snapshotId, password },
    ),

  listUsers: () =>
    request<
      Array<{
        id: string;
        email: string;
        displayName: string;
        role: string;
        mfaEnabled: boolean;
        active: boolean;
        lastLoginAt: string | null;
      }>
    >("GET", "/api/admin/users"),

  createUser: (data: {
    email: string;
    displayName: string;
    role: string;
    password: string;
    ownerPassword: string;
  }) => request<{ id: string }>("POST", "/api/admin/users", data),

  updateUser: (
    id: string,
    data: {
      displayName?: string;
      role?: string;
      active?: boolean;
      resetMfa?: boolean;
      ownerPassword: string;
    },
  ) => request<{ ok: boolean }>("PATCH", `/api/admin/users/${id}`, data),

  listOrganizations: () =>
    request<{
      items: Array<{
        id: string;
        code: string;
        displayName: string;
        status: string;
        createdAt: string;
        updatedAt: string;
      }>;
    }>("GET", "/api/admin/organizations"),

  createOrganization: (data: { code: string; displayName: string }) =>
    request<{
      id: string;
      code: string;
      displayName: string;
      status: string;
      planId: string;
    }>("POST", "/api/admin/organizations", data),

  listMemberships: () =>
    request<{
      items: Array<{
        id: string;
        organizationId: string;
        userId: string;
        email: string;
        displayName: string;
        role: string;
        status: string;
        userActive: boolean;
        invitedAt: string | null;
        activatedAt: string | null;
        updatedAt: string;
      }>;
    }>("GET", "/api/admin/memberships"),

  inviteMembership: (data: {
    email: string;
    displayName: string;
    role: string;
    password?: string;
    actorPassword: string;
  }) => request<{ id: string; userId: string }>("POST", "/api/admin/memberships/invite", data),

  activateMembership: (id: string, actorPassword: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/memberships/${id}/activate`, {
      actorPassword,
    }),

  updateMembershipRole: (id: string, role: string, actorPassword: string) =>
    request<{ ok: boolean }>("PATCH", `/api/admin/memberships/${id}`, {
      role,
      actorPassword,
    }),

  suspendMembership: (id: string, actorPassword: string) =>
    request<{ ok: boolean; sessionsRevoked: number }>(
      "POST",
      `/api/admin/memberships/${id}/suspend`,
      { actorPassword },
    ),

  removeMembership: (id: string, actorPassword: string) =>
    request<{ ok: boolean; sessionsRevoked: number }>(
      "POST",
      `/api/admin/memberships/${id}/remove`,
      { actorPassword },
    ),

  getCurrentEntitlements: () =>
    request<{
      organizationId: string;
      planId: string;
      planCode: string;
      planDisplayName: string;
      notes: string | null;
      limits: {
        maxActiveSites: number | null;
        maxActiveMembers: number | null;
        maxStorageBytes: number | null;
      };
      features: { publish: boolean; bundleGenerate: boolean; sponsored: boolean };
      usage: {
        activeSites: {
          usage: number;
          max: number | null;
          warnPercent: number;
          warning: boolean;
          exceeded: boolean;
        };
        activeMembers: {
          usage: number;
          max: number | null;
          warnPercent: number;
          warning: boolean;
          exceeded: boolean;
        };
        storageBytes: {
          usage: number;
          max: number | null;
          warnPercent: number;
          warning: boolean;
          exceeded: boolean;
        };
      };
      overrides: Record<string, unknown>;
      assignedAt: string;
      updatedAt: string;
    }>("GET", "/api/admin/entitlements/current"),

  getOrganizationEntitlements: (organizationId: string) =>
    request<{
      organizationId: string;
      planId: string;
      planCode: string;
      planDisplayName: string;
      notes: string | null;
      limits: {
        maxActiveSites: number | null;
        maxActiveMembers: number | null;
        maxStorageBytes: number | null;
      };
      features: { publish: boolean; bundleGenerate: boolean; sponsored: boolean };
      usage: {
        activeSites: {
          usage: number;
          max: number | null;
          warnPercent: number;
          warning: boolean;
          exceeded: boolean;
        };
        activeMembers: {
          usage: number;
          max: number | null;
          warnPercent: number;
          warning: boolean;
          exceeded: boolean;
        };
        storageBytes: {
          usage: number;
          max: number | null;
          warnPercent: number;
          warning: boolean;
          exceeded: boolean;
        };
      };
      overrides: Record<string, unknown>;
      assignedAt: string;
      updatedAt: string;
    }>("GET", `/api/admin/entitlements/organizations/${organizationId}`),

  assignOrganizationEntitlements: (
    organizationId: string,
    data: {
      planId: string;
      overrideMaxActiveSites?: number | null;
      overrideMaxActiveMembers?: number | null;
      overrideMaxStorageBytes?: number | null;
      overrideAllowPublish?: boolean | null;
      overrideAllowBundleGenerate?: boolean | null;
      notes?: string | null;
    },
  ) =>
    request<{
      ok: boolean;
      entitlement: {
        organizationId: string;
        planId: string;
        planCode: string;
        planDisplayName: string;
      };
    }>("PUT", `/api/admin/entitlements/organizations/${organizationId}`, data),

  listCommercialPlans: () =>
    request<{
      items: Array<{
        id: string;
        code: string;
        displayName: string;
        description: string | null;
        maxActiveSites: number | null;
        maxActiveMembers: number | null;
        maxStorageBytes: number | null;
        allowPublish: boolean;
        allowBundleGenerate: boolean;
        warnSitesPercent: number;
        warnMembersPercent: number;
        warnStoragePercent: number;
        active: boolean;
      }>;
    }>("GET", "/api/admin/commercial-plans"),

  createCommercialPlan: (data: Record<string, unknown>) =>
    request<{ id: string }>("POST", "/api/admin/commercial-plans", data),

  updateCommercialPlan: (id: string, data: Record<string, unknown>) =>
    request<{ ok: boolean }>("PATCH", `/api/admin/commercial-plans/${id}`, data),

  listAllSites: () =>
    request<
      Array<{
        id: string;
        code: string;
        name: string;
        location: string | null;
        active: boolean;
        codeLocked: boolean;
      }>
    >("GET", "/api/admin/sites/all"),

  createSite: (data: { code: string; name: string; location?: string | null }) =>
    request<{ id: string }>("POST", "/api/admin/sites", data),

  updateSite: (
    id: string,
    data: Partial<{ code: string; name: string; location: string | null; active: boolean }>,
  ) => request<{ ok: boolean }>("PATCH", `/api/admin/sites/${id}`, data),

  listPortalBundleTemplates: () =>
    request<{
      templates: Array<{
        id: string;
        releaseId: string;
        sourceFilename: string;
        sha256: string;
        fileCount: number;
        status: string;
        isDefault: boolean;
        verifiedAt: string | null;
        notes: string | null;
        createdAt: string;
      }>;
    }>("GET", "/api/admin/portal-bundles/templates"),

  importPortalBundleTemplate: async (file: File, releaseId: string, notes?: string) => {
    const token = csrfToken ?? (await fetchCsrf());
    const form = new FormData();
    form.append("file", file);
    form.append("releaseId", releaseId);
    if (notes) form.append("notes", notes);
    const res = await fetch("/api/admin/portal-bundles/templates/import", {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const err = (await res.json()) as { error?: string };
        if (err.error) message = err.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(message, res.status);
    }
    return res.json() as Promise<{
      template: {
        id: string;
        releaseId: string;
        sourceFilename: string;
        sha256: string;
        fileCount: number;
        status: string;
        isDefault: boolean;
        verifiedAt: string | null;
        notes: string | null;
        createdAt: string;
      };
    }>;
  },

  verifyPortalBundleTemplate: (
    templateId: string,
    data: { releaseId: string; sha256: string; fileCount: number },
  ) =>
    request<{
      template: {
        id: string;
        releaseId: string;
        sourceFilename: string;
        sha256: string;
        fileCount: number;
        status: string;
        isDefault: boolean;
        verifiedAt: string | null;
        notes: string | null;
        createdAt: string;
      };
      authoritative: { releaseId: string; sha256: string; fileCount: number };
    }>("POST", `/api/admin/portal-bundles/templates/${templateId}/verify`, data),

  retirePortalBundleTemplate: (templateId: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/portal-bundles/templates/${templateId}/retire`),

  setDefaultPortalBundleTemplate: (templateId: string) =>
    request<{
      template: {
        id: string;
        releaseId: string;
        sourceFilename: string;
        sha256: string;
        fileCount: number;
        status: string;
        isDefault: boolean;
        verifiedAt: string | null;
        notes: string | null;
        createdAt: string;
      };
    }>("POST", `/api/admin/portal-bundles/templates/${templateId}/default`),

  createPortalBundleJob: (data: { siteId: string; templateId?: string }) =>
    request<{
      job: PortalBundleJob;
    }>("POST", "/api/admin/portal-bundles/jobs", data),

  listPortalBundleJobs: (siteId?: string) => {
    const q = siteId ? `?siteId=${encodeURIComponent(siteId)}` : "";
    return request<{ jobs: PortalBundleJob[] }>("GET", `/api/admin/portal-bundles/jobs${q}`);
  },

  listPortalBundleHistory: (siteId?: string) => {
    const q = siteId ? `?siteId=${encodeURIComponent(siteId)}` : "";
    return request<{ history: PortalBundleJob[] }>("GET", `/api/admin/portal-bundles/history${q}`);
  },

  revokePortalBundleArtifact: (artifactId: string) =>
    request<{ ok: boolean }>("POST", `/api/admin/portal-bundles/artifacts/${artifactId}/revoke`),

  downloadPortalBundleArtifact: async (artifactId: string) => {
    const res = await fetch(`/api/admin/portal-bundles/artifacts/${artifactId}/download`, {
      credentials: "include",
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const err = (await res.json()) as { error?: string };
        if (err.error) message = err.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(message, res.status);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition");
    let filename = "portal-bundle.zip";
    const match = disposition?.match(/filename="([^"]+)"/);
    if (match?.[1]) filename = match[1];
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  listResellerProfiles: (params?: { q?: string; state?: string }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.state) qs.set("state", params.state);
    const q = qs.toString();
    return request<{ items: ResellerProfileSummary[] }>(
      "GET",
      `/api/admin/reseller-profiles${q ? `?${q}` : ""}`,
    );
  },

  getResellerProfile: (id: string) =>
    request<ResellerProfileDetail>("GET", `/api/admin/reseller-profiles/${id}`),

  createResellerProfile: (data: {
    code: string;
    operationalName: string;
    whatsappE164?: string | null;
    supportPhone?: string | null;
    supportHours?: string | null;
    supportText?: string | null;
    tagline?: string | null;
    termsCompanyName?: string | null;
  }) => request<ResellerProfileSummary>("POST", "/api/admin/reseller-profiles", data),

  updateResellerProfile: (
    id: string,
    data: {
      version: number;
      operationalName?: string;
      whatsappE164?: string | null;
      supportPhone?: string | null;
      supportHours?: string | null;
      supportText?: string | null;
      tagline?: string | null;
      termsCompanyName?: string | null;
    },
  ) => request<ResellerProfileSummary>("PATCH", `/api/admin/reseller-profiles/${id}`, data),

  publishResellerProfile: (id: string) =>
    request<ResellerProfileSummary>("POST", `/api/admin/reseller-profiles/${id}/publish`, {}),

  archiveResellerProfile: (id: string) =>
    request<ResellerProfileSummary>("POST", `/api/admin/reseller-profiles/${id}/archive`, {}),

  uploadResellerLogo: async (id: string, file: File) => {
    const token = csrfToken ?? (await fetchCsrf());
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`/api/admin/reseller-profiles/${id}/logo`, {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const err = (await res.json()) as { error?: string };
        if (err.error) message = err.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(message, res.status);
    }
    return res.json() as Promise<ResellerProfileSummary>;
  },

  removeResellerLogo: (id: string) =>
    request<ResellerProfileSummary>("DELETE", `/api/admin/reseller-profiles/${id}/logo`),

  setResellerProfileSites: (id: string, siteIds: string[]) =>
    request<ResellerProfileDetail>("PUT", `/api/admin/reseller-profiles/${id}/sites`, {
      siteIds,
    }),

  listResellerProfileSnapshots: (id: string) =>
    request<{ items: ResellerProfileSnapshot[] }>(
      "GET",
      `/api/admin/reseller-profiles/${id}/snapshots`,
    ),

  rollbackResellerProfile: (id: string, snapshotId: string, password: string) =>
    request<ResellerProfileSummary>("POST", `/api/admin/reseller-profiles/${id}/rollback`, {
      snapshotId,
      password,
    }),

  listFleetInventory: () =>
    request<{ items: RouterInventoryRow[] }>("GET", "/api/admin/fleet/inventory"),

  getFleetInventory: (siteId: string) =>
    request<{
      inventory: RouterInventoryRow | null;
      site: { id: string; code: string; name: string };
    }>("GET", `/api/admin/fleet/inventory/${siteId}`),

  upsertFleetInventory: (
    siteId: string,
    data: {
      serialAssetTag?: string | null;
      managementHost?: string | null;
      routerModel?: string | null;
      routerosVersion?: string | null;
      hotspotProfileName?: string | null;
      portalDirectoryPath?: string | null;
      ring?: "pilot" | "canary" | "production";
      notes?: string | null;
      password?: string;
    },
  ) => request<RouterInventoryRow>("PUT", `/api/admin/fleet/inventory/${siteId}`, data),

  listFleetDeployments: (siteId?: string) => {
    const q = siteId ? `?siteId=${encodeURIComponent(siteId)}` : "";
    return request<{ items: FleetDeploymentRow[] }>("GET", `/api/admin/fleet/deployments${q}`);
  },

  getFleetDeployment: (id: string) =>
    request<FleetDeploymentRow>("GET", `/api/admin/fleet/deployments/${id}`),

  createFleetDeploymentIntent: (data: {
    siteId: string;
    bundleArtifactId: string;
    approvedRollbackArtifactId?: string | null;
    evidenceNotes?: string | null;
  }) =>
    request<FleetDeploymentRow>("POST", "/api/admin/fleet/deployments", data),

  recordFleetInstall: (
    id: string,
    data: {
      installedReleaseId: string;
      installedSha256?: string | null;
      evidenceNotes?: string | null;
    },
  ) =>
    request<FleetDeploymentRow>("POST", `/api/admin/fleet/deployments/${id}/record-install`, data),

  verifyFleetDeployment: (
    id: string,
    data: {
      verificationStatus: "verified_match" | "verified_mismatch" | "skipped";
      driftReason?: string | null;
      evidenceNotes?: string | null;
      password?: string;
    },
  ) => request<FleetDeploymentRow>("POST", `/api/admin/fleet/deployments/${id}/verify`, data),

  cancelFleetDeployment: (id: string) =>
    request<FleetDeploymentRow>("POST", `/api/admin/fleet/deployments/${id}/cancel`, {}),

  updateFleetRollbackDrill: (
    id: string,
    data: { rollbackDrillStatus: "not_started" | "drilled" | "waived"; password?: string },
  ) =>
    request<FleetDeploymentRow>("POST", `/api/admin/fleet/deployments/${id}/rollback-drill`, data),

  listFleetDrift: (scope?: "platform") => {
    const q = scope === "platform" ? "?scope=platform" : "";
    return request<{ items: FleetDriftRow[]; note: string }>("GET", `/api/admin/fleet/drift${q}`);
  },

  getObservabilitySummary: () =>
    request<ObservabilityOpsSummary>("GET", "/api/admin/observability/summary"),

  listObservabilityAggregates: (params?: {
    scope?: "platform" | "organization";
    from?: string;
    to?: string;
    site?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.site) qs.set("site", params.site);
    const q = qs.toString();
    return request<ObservabilityAggregatesResponse>(
      "GET",
      `/api/admin/observability/aggregates${q ? `?${q}` : ""}`,
    );
  },

  listObservabilityAlerts: () =>
    request<{ items: ObservabilityAlertEvent[] }>("GET", "/api/admin/observability/alerts"),

  evaluateObservabilityAlerts: () =>
    request<{
      evaluated: number;
      results: Array<{ alert: string; status: string }>;
    }>("POST", "/api/admin/observability/alerts/evaluate", {}),

  sendObservabilityTestAlert: (password: string) =>
    request<{
      alert: string;
      status: string;
      webhookConfigured: boolean;
    }>("POST", "/api/admin/observability/alerts/test", { password }),

  listRestoreDrills: () =>
    request<{ items: ObservabilityRestoreDrill[] }>("GET", "/api/admin/observability/restore-drills"),

  runRestoreDrill: (password: string) =>
    request<ObservabilityRestoreDrill>("POST", "/api/admin/observability/restore-drills", {
      password,
    }),

  runObservabilityRetention: () =>
    request<{
      aggregatesDeleted: number;
      auditDeleted: number;
      alertEventsDeleted: number;
    }>("POST", "/api/admin/observability/retention/run", {}),
};

export type KillSwitchAdminState = {
  enabled: boolean;
  active: boolean;
  reason: string | null;
  enabledAt: string | null;
  enabledByUserId: string | null;
  expiresAt: string | null;
  cacheMaxAgeSeconds: number;
  maxPropagationSeconds: number;
};

export type ContentDraftDiff = {
  contentId: string;
  kind: string;
  state: string;
  hasPublishedSnapshot: boolean;
  publishedVersion: number | null;
  fields: Array<{ field: string; draft: unknown; published: unknown; changed: boolean }>;
  draftPublicPreview: {
    title: string;
    body: string;
    type: string;
    dismissible: boolean;
    startsAt: string | null;
    endsAt: string | null;
  } | null;
};

export type ResellerProfileSnapshot = {
  id: string;
  version: number;
  logoPath: string | null;
  payloadChecksum: string;
  publishedAt: string;
  retainUntil: string;
  rollbackOfSnapshotId: string | null;
  publishedPayload: Record<string, unknown>;
};

export type ResellerProfileSummary = {
  id: string;
  code: string;
  operationalName: string;
  logoPath: string | null;
  hasStagingLogo: boolean;
  whatsappE164: string | null;
  supportPhone: string | null;
  supportHours: string | null;
  supportText: string | null;
  tagline: string | null;
  termsCompanyName: string | null;
  state: string;
  version: number;
  linkedSiteIds: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
};

export type ResellerProfileDetail = ResellerProfileSummary & {
  linkedSites: Array<{ id: string; code: string; name: string; active: boolean }>;
};

export type PortalBundleArtifact = {
  id: string;
  generatedFilename: string;
  artifactSha256: string;
  fileCount: number;
  byteSize: number;
  runtimeConfigSha256: string | null;
  createdAt: string;
};

export type PortalBundleJob = {
  id: string;
  sequence: number;
  siteId: string;
  siteCode: string;
  templateId: string;
  templateReleaseId: string;
  templateSha256: string;
  status: string;
  requestedAt: string;
  completedAt: string | null;
  failureCode: string | null;
  expiresAt: string | null;
  artifact: PortalBundleArtifact | null;
};

export type RouterInventoryRow = {
  id: string;
  organizationId: string;
  siteId: string;
  siteCode: string;
  siteName: string;
  serialAssetTag: string | null;
  managementHost: string | null;
  routerModel: string | null;
  routerosVersion: string | null;
  hotspotProfileName: string | null;
  portalDirectoryPath: string | null;
  ring: "pilot" | "canary" | "production";
  notes: string | null;
  updatedAt: string;
  createdAt: string;
};

export type FleetDeploymentRow = {
  id: string;
  organizationId: string;
  siteId: string;
  ring: string;
  phase: string;
  expectedTemplateReleaseId: string;
  expectedTemplateSha256: string;
  bundleJobId: string | null;
  bundleArtifactId: string | null;
  artifactFilename: string | null;
  artifactSha256: string | null;
  intendedAt: string;
  intendedBy: string;
  installedReleaseId: string | null;
  installedSha256: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  verificationStatus: string;
  evidenceNotes: string | null;
  approvedRollbackArtifactId: string | null;
  rollbackDrillStatus: string;
  driftReason: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FleetDriftRow = {
  organizationId: string;
  organizationCode: string | null;
  siteId: string;
  siteCode: string;
  siteName: string;
  ring: string;
  expectedReleaseId: string | null;
  expectedTemplateSha256: string | null;
  recordedInstalledReleaseId: string | null;
  verificationStatus: string | null;
  deploymentId: string | null;
  deploymentPhase: string | null;
  intendedAt: string | null;
  driftKind: string;
  driftLabel: string;
  driftReason: string | null;
};

export type ObservabilityOpsSummary = {
  disclaimerFa: string;
  disclaimerEn: string;
  readiness: {
    status: "ok" | "degraded" | "fail";
    ready: boolean;
    checks: { postgres: string; uploads: string; backup: string };
    backupAgeSeconds: number | null;
  };
  gauges: {
    backupAgeSeconds: number | null;
    killSwitchActive: boolean;
    fleetDriftSites: number;
    storageUsedPercent: number | null;
    uploadBytesUsed: number | null;
    uploadBytesFree: number | null;
  };
  retention: {
    aggregateRetentionDays: number;
    auditRetentionDays: number;
    alertEventRetentionDays: number;
  };
  restoreDrill: {
    lastSuccessAt: string | null;
    latest: ObservabilityRestoreDrill | null;
  };
  activation: {
    webhookConfigured: boolean;
    scrapeTokenConfigured: boolean;
  };
  at: string;
};

export type ObservabilityAggregateRow = {
  bucketUtc: string;
  siteCode: string;
  organizationId: string;
  httpStatus: string;
  sponsored: number;
  count: number;
};

export type ObservabilityAggregatesResponse = {
  disclaimer: string;
  disclaimerFa: string;
  disclaimerEn: string;
  scope: "organization" | "platform";
  organizationId?: string;
  from: string;
  to: string;
  items: ObservabilityAggregateRow[];
};

export type ObservabilityAlertEvent = {
  id: string;
  firedAt: string;
  alert: string;
  severity: string;
  status: string;
  details: Record<string, unknown>;
};

export type ObservabilityRestoreDrill = {
  id: string;
  recordedAt: string;
  ok: boolean;
  kind: "postgres";
  isolated: boolean;
  backupSha256: string | null;
  backupBasename: string | null;
  source: string;
  evidenceSummary: string;
  durationMs: number;
};

export function clearCsrfCache(): void {
  csrfToken = null;
}
